// react
import * as React from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import { useGetUserByEmailQuery } from '../../../api/recalc/endpoints/userApiSlice';

export function useUserMenuState() {
  const { t, i18n } = useTranslation('tool-bar');
  const dispatch = useDispatch();
  const [savedLanguage, setSavedLanguage] = useLocalStorage('locale', 'dk');
  const { session: sessionData } = useSessionAuth();
  const email = sessionData?.claims?.email || '';
  const { data: userData } = useGetUserByEmailQuery(email, { skip: !email });
  const timezone = userData?.TimeZone || '';

  // Sync i18n with saved language on mount
  React.useEffect(() => {
    if (savedLanguage && i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [savedLanguage, i18n]);

  // Display role label with translation support
  const currentHour = (() => {
    try {
      const formattedHour = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        hour12: false,
        timeZone: timezone || undefined,
      }).format(new Date());
      const parsedHour = Number(formattedHour);
      return Number.isNaN(parsedHour) ? new Date().getHours() : parsedHour;
    } catch {
      return new Date().getHours();
    }
  })();

  const greetingText =
    currentHour < 12
      ? t('toolbar.greeting.morning')
      : currentHour < 18
        ? t('toolbar.greeting.afternoon')
        : t('toolbar.greeting.evening');

  const handleLogout = () => {
    window.location.href = `/auth/end-session`;
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setSavedLanguage(lng);
    dispatch(
      showSnackbar({
        message:
          lng === 'en'
            ? 'Language changed to English'
            : 'Sprog ændret til Dansk',
        severity: 'success',
      }),
    );
  };

  return {
    t,
    email,
    timezone,
    greetingText,
    currentHour,
    currentLanguage: i18n.language,
    changeLanguage,
    handleLogout,
  };
}
